import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Link from "@mui/material/Link";
import { useTranslation } from "react-i18next";
import { type DocsPage, resolveDocsLanguage } from "@/lib/docsPages";

type Props = {
  agreedTerms: boolean;
  agreedPrivacy: boolean;
  agreedCancellation: boolean;
  onAgreedTermsChange: (value: boolean) => void;
  onAgreedPrivacyChange: (value: boolean) => void;
  onAgreedCancellationChange: (value: boolean) => void;
};

// 利用規約・プライバシーポリシーのラベル。Footer (components/layout/Footer.tsx) と同様、
// public/docs/{lng}/{page}.html を別タブで開く。実コンテンツが無い言語は
// resolveDocsLanguage が既定 (en) にフォールバックする。
function ExternalAgreementLabel({
  prefix,
  linkText,
  suffix,
  page,
  lng,
}: {
  prefix: string;
  linkText: string;
  suffix: string;
  page: DocsPage;
  lng: string;
}) {
  return (
    <span>
      {prefix}
      <Link
        href={`/docs/${resolveDocsLanguage(page, lng)}/${page}.html`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        {linkText}
      </Link>
      {suffix}
    </span>
  );
}

/**
 * 利用規約・プライバシーポリシー・キャンセルポリシーへの同意チェックボックス。
 * 購入者情報入力画面 (BuyerInfoStep) の「決済に進む」ボタン上部に配置する。
 */
export function AgreementCheckboxes({
  agreedTerms,
  agreedPrivacy,
  agreedCancellation,
  onAgreedTermsChange,
  onAgreedPrivacyChange,
  onAgreedCancellationChange,
}: Props) {
  const { t, i18n } = useTranslation();
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <FormControlLabel
        sx={{ alignItems: "flex-start" }}
        control={
          <Checkbox
            checked={agreedTerms}
            onChange={(e) => onAgreedTermsChange(e.target.checked)}
            color="secondary"
            sx={{ pt: 0 }}
          />
        }
        label={
          <ExternalAgreementLabel
            prefix={t("buyerInfo.agreeTermsPrefix")}
            linkText={t("buyerInfo.termsLinkText")}
            suffix={t("buyerInfo.agreeTermsSuffix")}
            page="terms-of-service"
            lng={i18n.language}
          />
        }
      />
      <FormControlLabel
        sx={{ alignItems: "flex-start" }}
        control={
          <Checkbox
            checked={agreedPrivacy}
            onChange={(e) => onAgreedPrivacyChange(e.target.checked)}
            color="secondary"
            sx={{ pt: 0 }}
          />
        }
        label={
          <ExternalAgreementLabel
            prefix={t("buyerInfo.agreePrivacyPrefix")}
            linkText={t("buyerInfo.privacyLinkText")}
            suffix={t("buyerInfo.agreePrivacySuffix")}
            page="privacy-policy"
            lng={i18n.language}
          />
        }
      />
      <FormControlLabel
        sx={{ alignItems: "flex-start" }}
        control={
          <Checkbox
            checked={agreedCancellation}
            onChange={(e) => onAgreedCancellationChange(e.target.checked)}
            color="secondary"
            sx={{ pt: 0 }}
          />
        }
        label={t("buyerInfo.agreeCancellation")}
      />
    </Box>
  );
}
